/**
 * Inline playable HTML for Work artifact Files (and leftover live artifacts
 * that have not materialized yet). Replaces the document file card for .html
 * deliverables so a game or app is playable in the thread.
 */
import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { filesApi, coworkerApi } from '@/lib/api';
import { openMessageFile } from '@/constants/utils/files';
import { isHtmlCodeFile } from '@/constants/utils/code_file';
import {
  coworkerArtifactAsFile,
  isHtmlArtifact,
} from '@/components/Coworker/coworkerArtifactTypes';
import HtmlPlayablePreview from '@/components/Coworker/HtmlPlayablePreview';

async function loadHtmlFromFile(file) {
  const fileId = file?.id || file?._id;
  if (fileId) {
    try {
      const payload = await filesApi.fetchCodeText(fileId);
      if (payload?.text) return payload.text;
    } catch { /* fall through to the CDN */ }
  }
  const url = file?.cdnPath || file?.url || file?.uri;
  if (url) {
    const response = await fetch(url);
    if (response.ok) return response.text();
  }
  throw new Error('missing-file');
}

async function loadHtmlFromArtifact(taskId, artifact) {
  const downloadUrl = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path);
  if (downloadUrl) {
    try {
      const response = await fetch(downloadUrl);
      if (response.ok) return response.text();
    } catch { /* fall through to the authenticated proxy */ }
  }
  const response = await coworkerApi.fetchArtifactBinary(taskId, artifact.path, { asProxy: true });
  if (!response.ok) throw new Error('Download failed');
  return response.text();
}

function FileHtmlCard({ file }) {
  const loadHtml = useCallback(() => loadHtmlFromFile(file), [file]);
  const expand = useCallback(() => {
    openMessageFile(file);
  }, [file]);

  return (
    <HtmlPlayablePreview
      name={file.name || file.originalName}
      sourceKey={file.id || file._id || file.cdnPath || file.name}
      loadHtml={loadHtml}
      onExpand={expand}
    />
  );
}

function ArtifactHtmlCard({ artifact, taskId }) {
  const loadHtml = useCallback(
    () => loadHtmlFromArtifact(taskId, artifact),
    [artifact, taskId],
  );
  const expand = useCallback(async () => {
    const url = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path).catch(() => null);
    const file = coworkerArtifactAsFile(artifact, url || '');
    openMessageFile(file);
  }, [artifact, taskId]);

  return (
    <HtmlPlayablePreview
      name={artifact.name || artifact.path}
      sourceKey={`${taskId}|${artifact.path}|${artifact.size || 0}`}
      loadHtml={loadHtml}
      onExpand={expand}
    />
  );
}

export default function CoworkerHtmlPreview({ files, artifacts, taskId }) {
  const htmlFiles = useMemo(
    () => (Array.isArray(files) ? files.filter((file) => file && isHtmlCodeFile(file)) : []),
    [files],
  );
  const htmlArtifacts = useMemo(
    () => (Array.isArray(artifacts) ? artifacts.filter((artifact) => isHtmlArtifact(artifact)) : []),
    [artifacts],
  );

  if (htmlFiles.length === 0 && htmlArtifacts.length === 0) return null;

  return (
    <View style={styles.container}>
      {htmlFiles.map((file) => (
        <FileHtmlCard key={file.id || file._id || file.cdnPath || file.name} file={file} />
      ))}
      {htmlArtifacts.map((artifact) => (
        <ArtifactHtmlCard
          key={artifact.path || artifact.name}
          artifact={artifact}
          taskId={taskId}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
    marginTop: 8,
    maxWidth: 560,
  },
});
